import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MuiTelInput } from "mui-tel-input";
import apiService from "../../../service/Api";

export interface IContact {
  id: string | number;
  first_name?: string;
  last_name?: string;
  email: string | null;
  phone?: string | null;
  contact_type: "individual" | "company";
}

export default function ContactSelector({
  value,
  onChange,
}: {
  value: IContact | null;
  onChange: (contact: IContact | null) => void;
}) {
  const [options, setOptions] = useState<IContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const [newContact, setNewContact] = useState<Partial<IContact>>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    contact_type: "individual",
  });

  // Fetch contacts
  const fetchContacts = async (term: string) => {
    if (term.length < 3) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const res = await apiService.get<{ results: IContact[] }>(
        `/api/contacts/contact/?contact_type=individual&search=${term}`
      );
      setOptions(res.data.results || []);
    } catch (e) {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => fetchContacts(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Save new contact
  const handleSaveNewContact = async () => {
    setSaving(true);
    try {
      const res = await apiService.post<IContact>(
        "/api/contacts/contact/",
        newContact
      );

      toast.success("Contact created!");

      const saved = res.data;
      setOptions((prev) => [...prev, saved]);
      onChange(saved);
      setOpenAddDialog(false);
    } catch (error: any) {
      toast.error("Failed to create contact");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Autocomplete */}
      <Autocomplete
        fullWidth
        value={value}
        options={options}
        loading={loading}
        getOptionLabel={(option) =>
          `${option.first_name || ""} ${option.last_name || ""}`.trim()
        }
        noOptionsText={
          <Box
            onClick={() => setOpenAddDialog(true)}
            sx={{ cursor: "pointer" }}
          >
            <Typography color="primary">➕ Add New Contact</Typography>
          </Box>
        }
        onChange={(e, val) => onChange(val)}
        onInputChange={(e, val) => setSearchTerm(val)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Contact Person"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* Add Contact Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
        <DialogTitle>Add New Contact</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="First Name"
            value={newContact.first_name}
            onChange={(e) =>
              setNewContact((p) => ({ ...p, first_name: e.target.value }))
            }
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Last Name"
            value={newContact.last_name}
            onChange={(e) =>
              setNewContact((p) => ({ ...p, last_name: e.target.value }))
            }
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Email"
            value={newContact.email}
            onChange={(e) =>
              setNewContact((p) => ({ ...p, email: e.target.value }))
            }
            sx={{ mb: 2 }}
          />

          <MuiTelInput
            fullWidth
            label="Phone Number"
            value={newContact.phone ?? ""}
            onChange={(value) =>
              setNewContact((prev) => ({ ...prev, phone: value }))
            }
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveNewContact} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
